import React, { useCallback, useEffect, useState } from 'react';
import { namesApi } from '../../api/names';
import AtlasIcon from '../icons/AtlasIcon';
import { CLOSE_NAMES } from '../../utils/writerCopy';
import './NameWizardPanel.css';

const MIN_WIDTH = 340;
const MAX_WIDTH = 520;
const DEFAULT_WIDTH = 400;

const KINDS = [
  { id: 'character', label: 'Character' },
  { id: 'place', label: 'Place' },
  { id: 'object', label: 'Object' },
  { id: 'other', label: 'Other' },
];

async function copyText(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) { /* fall through */ }
  return false;
}

/**
 * NameWizardPanel — docked right-side naming helper.
 * Description + kind + count in; 3–12 suggested names out.
 * Does not write Book Notes. Click a name to copy it.
 */
export default function NameWizardPanel({
  storyId,
  width = DEFAULT_WIDTH,
  onWidthChange,
  onClose,
}) {
  const [kind, setKind] = useState('character');
  const [kindLabel, setKindLabel] = useState('');
  const [description, setDescription] = useState('');
  const [count, setCount] = useState(6);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [copied, setCopied] = useState(null);
  const [isResizing, setIsResizing] = useState(false);

  const handleResizeMove = useCallback((e) => {
    if (!isResizing || !onWidthChange) return;
    const next = window.innerWidth - e.clientX;
    onWidthChange(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, next)));
  }, [isResizing, onWidthChange]);

  const handleResizeEnd = useCallback(() => setIsResizing(false), []);

  useEffect(() => {
    if (!isResizing) return undefined;
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  const handleSuggest = async (e) => {
    e.preventDefault();
    if (!storyId || description.trim().length < 8) {
      setError('Describe what you’re naming — a sentence or two is enough.');
      return;
    }
    setBusy(true);
    setError(null);
    setSuggestions([]);
    try {
      const data = await namesApi.suggest(storyId, {
        description: description.trim(),
        kind,
        kindLabel: kind === 'other' ? kindLabel.trim() || null : null,
        count: Math.max(3, Math.min(12, Number(count) || 6)),
      });
      setSuggestions(data?.suggestions || []);
      if (!(data?.suggestions || []).length) {
        setError('Nothing came back — try a slightly fuller description.');
      }
    } catch (err) {
      setError(err.message || 'Could not suggest names.');
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async (name) => {
    const ok = await copyText(name);
    if (ok) {
      setCopied(name);
      setTimeout(() => setCopied((c) => (c === name ? null : c)), 1400);
    }
  };

  return (
    <aside
      className="name-wizard"
      style={{ width }}
      aria-label="Name suggestions"
    >
      <div
        className={`name-wizard__resize ${isResizing ? 'is-active' : ''}`}
        onMouseDown={(e) => { e.preventDefault(); setIsResizing(true); }}
      />
      <header className="name-wizard__header">
        <div className="name-wizard__title">
          <AtlasIcon name="spark" size={16} />
          <h2>Names</h2>
        </div>
        <button type="button" className="name-wizard__close" onClick={onClose} aria-label={CLOSE_NAMES}>
          ×
        </button>
      </header>

      {!storyId ? (
        <p className="name-wizard__hint">Select a story first — suggestions stay in that book’s register.</p>
      ) : (
        <form className="name-wizard__form" onSubmit={handleSuggest}>
          <fieldset className="name-wizard__kinds">
            <legend>What needs a name</legend>
            <div className="name-wizard__kind-row">
              {KINDS.map((k) => (
                <label key={k.id} className={`name-wizard__kind ${kind === k.id ? 'is-active' : ''}`}>
                  <input
                    type="radio"
                    name="name-kind"
                    value={k.id}
                    checked={kind === k.id}
                    onChange={() => setKind(k.id)}
                  />
                  {k.label}
                </label>
              ))}
            </div>
          </fieldset>

          {kind === 'other' && (
            <label className="name-wizard__field">
              What is it?
              <input
                type="text"
                value={kindLabel}
                onChange={(e) => setKindLabel(e.target.value)}
                placeholder="ship, spell, newspaper, street…"
              />
            </label>
          )}

          <label className="name-wizard__field">
            Description
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              placeholder="A morally compromised detective who works alone. Old money, New England restraint."
            />
          </label>

          <label className="name-wizard__field name-wizard__field--inline">
            How many suggestions
            <input
              type="number"
              min={3}
              max={12}
              value={count}
              onChange={(e) => setCount(e.target.value)}
            />
          </label>

          {error && <p className="name-wizard__error">{error}</p>}

          <button type="submit" className="name-wizard__submit" disabled={busy}>
            {busy ? 'Suggesting…' : 'Suggest names'}
          </button>
        </form>
      )}

      {suggestions.length > 0 && (
        <ul className="name-wizard__list">
          {suggestions.map((s) => (
            <li key={s.name}>
              <button type="button" className="name-wizard__hit" onClick={() => handleCopy(s.name)}>
                <span className="name-wizard__name">{s.name}</span>
                {s.note && <span className="name-wizard__note">{s.note}</span>}
                <span className="name-wizard__copy">
                  {copied === s.name ? 'Copied' : 'Copy'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}

export { MIN_WIDTH as NAMES_MIN, MAX_WIDTH as NAMES_MAX, DEFAULT_WIDTH as NAMES_DEFAULT };
